import { action, observable, reaction, runInAction } from 'mobx';
import { toastr } from 'react-redux-toastr';

import { invoiceController } from './invoiceController';
import APPConf from '../../../../lib/config/common';
import {
  CreateOrderInvoiceRequest,
  SendInvoiceToSmsRequest,
  GetOrderInvoicesRequest,
  ManageInvoiceRequest,
  GetQRCodeRequest,
  GetQRCodeResponse,
  SendSmsWithBonusRequest,
  InvoicePositionFilterInterface,
  tBankHoldRequest,tBankRefundRequest,
} from '../../api/invoiceApi';
import { getTotalAmountString } from '../../../core/utils/invoices';
import { ContentType } from 'modules/notifications/api/socket.types';
import { INVOICE_DISCARDED, INVOICE_PAID } from 'modules/core/constants/ref-values';

export interface Invoice {
  comment?: string,
  createdByUserId?: string,
  createdDate?: string,
  id: string,
  invoiceAmount: number,
  invoiceDiscount?: number,
  lastChangedByUserId?: string,
  lastChangedDate?: string,
  number: string,
  orderId: string,
  paidAmount?: number,
  partnerLegalId?: string,
  partnerLegalName?: string,
  paymentDate?: string,
  paymentMethodRefValueId?: number,
  paymentMethodRefValueName?: string,
  priceListId?: string,
  priceListName?: string,
  sourceRefValueId?: number,
  sourceRefValueName?: number,
  statusRefValueId?: number,
  statusRefValueName?: string,
  totalAmount?: string,
  nds: number,
  ndsAmount: number,
  bonusReceived?: number,
  bonusSpent?: number,
  partnerFee?: number,
  avtoappFee?: number,
  avtoappFeePercent?: number,
  formUrlSmsSendingDate?: string,

  percentageDiscount?: number, // процент скидки, если есть - рассчётиное поле
  justCreated?: boolean,

  // поля для мультиплатежей:
  dealId?: string,
  multisplitPaymentStatusRefValueId?: number,
  multisplitPaymentStatusRefValueName?: string,
}

export interface InvoicePosition {
  id?: string,
  priceListItemId: string,
  positionId: string,
  name: string,
  shortName: string,
  classifier1RefValueName: string,
  classifier2RefValueName: string,
  vendorCode?: string,
  currentPrice: number,
  price: number,
  quantity: number,
  discount?: number,
  amount?: number, // расчётное поле, не пишем в базу
  discountedAmount?: number, // расчётное поле, не пишем в базу
}

export interface InvoicesStore {
  invoices: Invoice[],
  invoicesRequest: GetOrderInvoicesRequest,
  error: any,
  currentInvoice: Invoice,
  shortMobileMode: boolean,
  invoicePositions: InvoicePosition[],
  invoicePositionsById: string, // по этому invoiceId в invoicePositions находятся данные
  invoicePositionsLoaded: boolean, // true после того, как getOrderInvoicePositions завершил запрос
  isLoadingInvoices: boolean, // true пока выполняется getInvoices (для предотвращения дублирующих запросов)
  partnerLegalId?: string,
  qrCode: GetQRCodeResponse,
  getQRCodeRequest: GetQRCodeRequest,

  invoicePositionFilter?: InvoicePositionFilterInterface
}

export const invoicesStore: InvoicesStore = observable({
  invoices: [],
  invoicesRequest: null,
  error: null,
  currentInvoice: null,
  shortMobileMode: false,
  invoicePositions: [],
  invoicePositionsById: null,
  invoicePositionsLoaded: false,
  isLoadingInvoices: false,
  partnerLegalId: undefined,

  qrCode: null,
  getQRCodeRequest: null,
  invoicePositionFilter: null,
} as InvoicesStore);

export interface EditOrderInvoiceRequest {
  partnerLegalId: string,
  invoiceAmount: number,
  invoiceDiscount: number,
  comment: string,
  priceListId: string,
  invoicePositions?: InvoicePosition[],
}

export const invoicesActions = {
  getInvoices: async (request: GetOrderInvoicesRequest): Promise<void> => {
    if (invoicesStore.isLoadingInvoices) {
      return;
    }
    runInAction(() => {
      invoicesStore.isLoadingInvoices = true;
    });
    try {
      const response = await invoiceController.getOrderInvoices(request);
      invoicesActions.handleError(response);
      runInAction(() => {
        invoicesStore.invoicesRequest = request;
        invoicesStore.invoices = response.data;
      });
    } finally {
      runInAction(() => {
        invoicesStore.isLoadingInvoices = false;
      });
    }
  },

  clearInvoices: async (): Promise<void> => {
    runInAction(() => {
      invoicesStore.invoicesRequest = null;
      invoicesStore.invoices = [];
      invoicesStore.isLoadingInvoices = false;
    });
  },

  getInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.getOrderInvoice(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
  },

  createInvoice: async (request: CreateOrderInvoiceRequest): Promise<void> => {
    const response = await invoiceController.createInvoice(request);
    if (response.errorData) {
      invoicesActions.handleError(response);
    } else {
      invoicesStore.currentInvoice = response.data;
    }
  },

  // создание счетов для ШМ
  createInvoiceAutomated: async (orderId: string, shortMobileMode = false): Promise<void> => {
    const response = await invoiceController.createInvoiceAutomated({ orderId });
    if (response.errorData) {
      invoicesActions.handleError(response);
    } else {
      invoicesStore.currentInvoice = response.data;
      invoicesStore.shortMobileMode = shortMobileMode;
    }
  },

  // далее автозаполнение счёта
  // в мае 2025 года пока сделали только автозаполнение партнёра и прайса
  createInvoiceAutofill: async (orderId: string, serviceId): Promise<string> => {
    // eslint-disable-next-line no-var
    var response:any = null;
    if (serviceId) {
      response = await invoiceController.createInvoiceAutomated({ orderId, onlyFirstFill: true });
    }
    else {
      response = await invoiceController.createInvoiceAutofill(orderId);
    };
    if (response.errorData) {
      invoicesActions.handleError(response);
      return null;
    } else {

      if (invoicesStore.invoicesRequest?.orderId === orderId) {
        const x = [...invoicesStore.invoices];
        x.push(response.data);
        runInAction(() => {
          invoicesStore.invoices = x;
          invoicesActions.setCurrentInvoice(response.data, true);
        });
        return response.data.id;
      }
    };
  },

  editInvoice: async (id: string, request: EditOrderInvoiceRequest): Promise<void> => {
    const response = await invoiceController.editInvoice(id, {
      ...request,
      invoicePositions: [...invoicesStore.invoicePositions],
    });
    invoicesActions.handleError(response);
    // Сервер не возвращает percentageDiscount (расчётное поле фронта),
    // поэтому вычисляем его сами и передаём в setCurrentInvoice
    const fullAmount = Math.round(invoicesStore.invoicePositions.reduce(
      (acc, v) => acc + (Number(v.amount ?? (v.currentPrice ?? 0) * (v.quantity ?? 0)) || 0), 0
    ) * 100) / 100;
    const newInvoiceAmount = response.data.invoiceAmount ?? 0;
    const newPercentageDiscount = fullAmount > 0 
      ? Math.round(100 * 100 * (fullAmount - newInvoiceAmount) / fullAmount) / 100
      : 0;
  
    invoicesActions.setCurrentInvoice({ 
      ...response.data, 
      percentageDiscount: newPercentageDiscount, 
    });
  },
  
  setPaidInvoice: (content: ContentType): void => {
    if (!content?.orderId || !content?.invoiceId || !content?.invoiceStatusRefValueId) {
      return;
    }

    if (invoicesStore.invoicesRequest?.orderId !== content.orderId) {
      return;
    }

    // Сначала найдём индекс и старый статус БЕЗ мутации
    const invoiceIndex = invoicesStore.invoices.findIndex(inv => inv.id === content.invoiceId);
    if (invoiceIndex === -1) return;

    const oldStatus = invoicesStore.invoices[invoiceIndex].statusRefValueId;
    const newStatus = content.invoiceStatusRefValueId;
    const invoiceNumber = invoicesStore.invoices[invoiceIndex].number;

    // Теперь безопасно обновляем состояние
    runInAction(() => {
      const updatedInvoice: Invoice = {
        ...invoicesStore.invoices[invoiceIndex],
        statusRefValueId: newStatus,
      };

      invoicesStore.invoices[invoiceIndex] = updatedInvoice;

      if (invoicesStore.currentInvoice?.id === content.invoiceId) {
        invoicesStore.currentInvoice = updatedInvoice;
      }
    });

    if (newStatus === INVOICE_PAID && oldStatus !== INVOICE_PAID) {
      toastr.success(`✅ Счёт №${ invoiceNumber } оплачен`);
    }

    if (newStatus === INVOICE_DISCARDED && oldStatus !== INVOICE_DISCARDED) {
      toastr.error(`❌ Счёт №${ invoiceNumber } отклонён`);
    }    
  },
  // далее идёт спец.метод для изменения служебных полей счёта
  manageInvoice: async (id: string, request: ManageInvoiceRequest): Promise<void> => {
    const response = await invoiceController.manageInvoice(id, request);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
  },

  deleteInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.deleteInvoice(id);
    if (response.errorData) {
      invoicesActions.handleError(response);
    } else {
      runInAction(() => {
        invoicesStore.invoices = invoicesStore.invoices.filter((v)=> v.id !== id);
      });
      invoicesActions.clearCurrentInvoice(); // (response.data);
    };
  },

  // функция правит счёт в списке 
  setInvoiceInInvoiceList: async (data: Invoice): Promise<void> => {
    if (data?.id) {
      const x = invoicesStore.invoices.filter((v)=> v.id !== data?.id);
      x.push(data);
      runInAction(() => {
        invoicesStore.invoices = x;
      });
    }
  },

  restoreInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.restoreInvoice(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  sendInvoice: async (id: string, sendPush = true): Promise<void> => {
    const response = await invoiceController.sendInvoice(id, sendPush);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  acceptInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.acceptInvoice(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  sendInvoiceToSms: async (id: string, data: SendInvoiceToSmsRequest): Promise<void> => {
    const response = await invoiceController.sendInvoiceToSms(id, data);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  revokeInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.revokeInvoice(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  retractLastPaymentInInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.retractLastPaymentInInvoice(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  payInCashInvoice: async (id: string, viaTerminal: boolean = false,
    bonusToSpend: number, sms: string ): Promise<void> => {
    const response = await invoiceController.payInCashInvoice(id, viaTerminal, bonusToSpend, sms);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  payToAccountInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.payToAccountInvoice(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  affirmPaidToAccount: async (id: string): Promise<void> => {
    const response = await invoiceController.affirmPaidToAccount(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  reformInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.reformInvoice(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },
  
  // операции с мультиплатежами
  // блокируем деньги
  tBankHoldPaymentInInvoice: async (id: string, request: tBankHoldRequest): Promise<void> => {
    const response = await invoiceController.tBankHoldPaymentInInvoice(id, request);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },
  
  // снимаем блокировку деньги
  tBankUnHoldPaymentInInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.tBankUnHoldPaymentInInvoice(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  // пропускаем суточное ожидание перед выплатой
  tBankSkipWaitPaymentInInvoice: async (id: string, request: tBankHoldRequest): Promise<void> => {
    const response = await invoiceController.tBankSkipWaitPaymentInInvoice(id, request);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  // всё на счёт автоапп 
  tBankPaymentRedirectToAvtoapp: async (id: string): Promise<void> => {
    const response = await invoiceController.tBankPaymentRedirectToAvtoapp(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },
  
  // возвращаем деньги
  tBankReturnMoneyPaymentInInvoice: async (id: string, request: tBankRefundRequest): Promise<void> => {
    const response = await invoiceController.tBankReturnMoneyPaymentInInvoice(id, request);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
    invoicesActions.setInvoiceInInvoiceList(response.data);
  },

  // конец операций с мультиплатежами Т-Банка

  closeInvoice: async (id: string): Promise<void> => {
    const response = await invoiceController.closeInvoice(id);
    invoicesActions.handleError(response);
    invoicesActions.setCurrentInvoice(response.data);
  },

  clearCurrentInvoice: action(() => {
    invoicesStore.currentInvoice = null;
    invoicesStore.shortMobileMode = false;
  }),

  clearError: action(() => {
    invoicesStore.error = null;
  }),

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      invoicesStore.error = errorData;
      throw errorData;
    }
  }),

  setCurrentInvoice: action((invoice: Invoice, justCreated: boolean = false) => {

    invoice.totalAmount = getTotalAmountString(invoice.invoiceAmount, invoice.invoiceDiscount);
    invoicesStore.currentInvoice = { ...invoice, justCreated };
  }),

  setNDS: action((nds, partnerLegalId) => {
    invoicesActions.setCurrentInvoice({ ...invoicesStore.currentInvoice, nds, partnerLegalId });
    invoicesActions.calcInvoicePosition();
  }),

  getOrderInvoicePositions: async (id: string): Promise<void> => {
    runInAction(() => {
      invoicesStore.invoicePositionsById = id;
      invoicesStore.invoicePositionsLoaded = false; // сброс до завершения запроса
    });
    const response = await invoiceController.getOrderInvoicePositions(id);
    if (response.errorData) {
      invoicesStore.invoicePositionsById = null;
      invoicesActions.handleError(response);
    }
    else {
      runInAction(() => {
        invoicesStore.invoicePositionsById = id;
        invoicesStore.invoicePositions = response.data;
        invoicesStore.invoicePositionsLoaded = true; // данные загружены (даже если пусто)
        invoicesActions.calcInvoicePosition();
      });
    };
  },

  addInvoicePosition: action((invoicePosition: InvoicePosition) => {
    const ind = invoicesStore.invoicePositions.findIndex((v) => v.priceListItemId === invoicePosition.priceListItemId);
    if (ind >= 0) {
      const x = invoicesStore.invoicePositions[ind];
      x.quantity += invoicePosition.quantity;
      invoicesStore.invoicePositions[ind] = x;
    } else {
      invoicesStore.invoicePositions.push(invoicePosition);
    }
    invoicesActions.calcInvoicePosition();
  }),

  setDiscountToInvoicePosition: action((localDiscount) => {
    invoicesStore.invoicePositions = invoicesStore.invoicePositions.map((v) => {
      return {
        ...v,
        discount: Math.round((v.amount ?? 0) * (localDiscount ?? 0)) / 100,
      };
    });
    invoicesActions.calcInvoicePosition();
  }),

  calcInvoicePosition: action(() => {
    // currentInvoice может быть null (init + clearCurrentInvoice); без защиты падает
    // с TypeError на доступе к .invoiceAmount. Позиции пересчитываем бессмысленно,
    // если счёт, к которому они относятся, не выбран.
    if (!invoicesStore.currentInvoice) return;
    invoicesStore.invoicePositions = invoicesStore.invoicePositions.map((v) => {
      return {
        ...v,
        amount: Math.round((v.currentPrice ?? 0) * (v.quantity ?? 0) * 100) / 100,
        discountedAmount: Math.round((((v.currentPrice ?? 0) * (v.quantity ?? 0)) - (v.discount ?? 0)) * 100) / 100,
      };
    });
    invoicesStore.currentInvoice.invoiceAmount = Math.round(invoicesStore.invoicePositions.reduce(
      (accumulator, currentValue) => accumulator + (currentValue.discountedAmount ?? 0),
      0
    ) * 100) / 100;

    invoicesStore.currentInvoice.ndsAmount = Math.round((invoicesStore.currentInvoice.invoiceAmount
       - invoicesStore.currentInvoice.invoiceDiscount) * invoicesStore.currentInvoice.nds) / 100;

    const fullAmount = Math.round(invoicesStore.invoicePositions.reduce(
      (accumulator, currentValue) => accumulator + (currentValue.amount ?? 0),
      0
    ) * 100) / 100;
    
    if (invoicesStore.currentInvoice.invoiceDiscount === invoicesStore.currentInvoice.invoiceAmount &&
       invoicesStore.currentInvoice.invoiceDiscount > 0) {
      invoicesStore.currentInvoice.percentageDiscount = 100;
    }
    else {
      invoicesStore.currentInvoice.percentageDiscount = fullAmount 
        ? Math.round( 100* 100*(fullAmount - invoicesStore.currentInvoice.invoiceAmount)/ fullAmount)/ 100 
        : 0;
    }
  }),

  deleteInvoicePosition: action((priceListItemId: string) => {
    invoicesStore.invoicePositions = invoicesStore.invoicePositions
      .filter((v) => v.priceListItemId !== priceListItemId);
    invoicesActions.calcInvoicePosition();
  }),

  deleteAllInvoicePositions: action(() => {
    invoicesStore.invoicePositions = [];
  }),

  clearInvoicePositions: action(() => {
    invoicesStore.invoicePositionsById = null;
    invoicesStore.invoicePositions = [];
    invoicesStore.invoicePositionsLoaded = false;
  }),

  resetInvoicePositionsFor: action((id: string) => {
    invoicesStore.invoicePositionsById = id;
    invoicesStore.invoicePositions = [];
    invoicesStore.invoicePositionsLoaded = false;
  }),

  setInvoicePositionsToStore: action((newInvoicePositions: InvoicePosition[]) => {
    runInAction(() => {
      invoicesStore.invoicePositions = newInvoicePositions;
      invoicesActions.calcInvoicePosition();
    });
  }),

  // блок - юр.лицо по исполнителю и сервису
  // автоматически заполняем юр.лицо при оформлении счёта, привязанное к исполнителю к указанному сервису
  // функция ниже только достаёт это юр.лицо из базы
  getPartnerLegalUser: async (data) => {
    const response = await invoiceController.getPartnerLegalUser(data);
    action(() => {
      if ((response.errorData) && (response.errorData.errorCode !== 'ENTITY_NOT_FOUND')) {
        invoicesStore.error = response.errorData;
      }
      invoicesStore.partnerLegalId = response.data?.partnerLegalId ?? undefined;
    })();
  },

  clearPartnerLegalUser: async () => {
    action(() => {
      invoicesStore.partnerLegalId = undefined;
    })();
  },
  // конец блока - юр.лицо по исполнителю и сервису

  getQrCode: async (request: GetQRCodeRequest) => {
    runInAction(() => {
      invoicesStore.getQRCodeRequest = request;
    });
    const response = await invoiceController.getQrCode(request);
    if (response.errorData) {
      invoicesActions.clearQrCode();
      return;
    };
    action(() => {
      invoicesStore.qrCode = response.data;
    })();
  },

  clearQrCode: async () => {
    action(() => {
      invoicesStore.qrCode = null;
      invoicesStore.getQRCodeRequest = null;
    })();
  },

  sendSmsWithBonus: async (request: SendSmsWithBonusRequest) => {
    const response = await invoiceController.sendSmsWithBonus(request);
    invoicesActions.handleError(response);
    /*if (response.errorData) {
      return;
    };
    action(() => {
      invoicesStore.???? = response.data;
    })();*/
  },

  setInvoicePositionFilter: action((invoicePositionFilter: InvoicePositionFilterInterface) => {
    invoicesStore.invoicePositionFilter = invoicePositionFilter;
  }),
  clearInvoicePositionFilter: action(() => {
    invoicesStore.invoicePositionFilter = null;
  }),
};

// 1. Реакция на показ обычных ошибок
// Сохраняем в переменную, чтобы анализатор не ругался на отсутствие dispose
const disposeInvoiceErrorReaction = reaction(
  () => invoicesStore.error, 
  (error) => { // Используем аргумент error, чтобы не дергать стор повторно
    if (error && error.errorCode !== 'INELIGIBLE_STATE') {
      toastr.error(APPConf.errorHdr, error.message);
    }
  }
);

// 2. Реакция на изменение счёта извне (INELIGIBLE_STATE)
const disposeIneligibleReaction = reaction(
  () => invoicesStore.error?.errorCode === 'INELIGIBLE_STATE', 
  (isIneligible) => {
    if (isIneligible && invoicesStore.currentInvoice != null) {
      // Убрали async/await. Просто запускаем обновление в фоне
      invoicesActions.getInvoice(invoicesStore.currentInvoice.id);
      toastr.error('Счёт был изменён.', 'Данные по счёту обновлены.', { timeout: 3000 });
    }
  }
);

export const disposeInvoicesStore = () => {
  disposeInvoiceErrorReaction();
  disposeIneligibleReaction();
};